import { CompanyResponseDto, CompanyType } from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import companyService from '@planning/services/companyService'
import { CustomerAutocompleteViewStore } from '@planning/stores/customer/CustomerAutocompleteViewStore'
import { useTranslate } from '@tolgee/react'
import { ColoredLabel, TomAutoComplete, TomAutoCompleteProps } from '@tom-ui/ui'
import { AxiosError } from 'axios'
import { matchSorter } from 'match-sorter'
import { observer, useLocalStore } from 'mobx-react-lite'
import { FC, useEffect, useState } from 'react'

export type CompanyAutocompleteProps = {
  selectedCustomer?: CompanyResponseDto | null
  handleOnChange?: (value?: CompanyResponseDto | null) => void
  filterByTypes?: CompanyType[]
  selectedCompanyShortName?: string | null
  selectedCompanyId?: number | null
  selectedReferenceId?: number | null
  dataCy?: string
  hasNoneOption?: boolean
  validate?: () => void
} & TomAutoCompleteProps<CompanyResponseDto>

export const CompanyAutocomplete: FC<CompanyAutocompleteProps> = observer(props => {
  const {
    selectedCustomer,
    handleOnChange,
    label,
    disabled,
    filterByTypes,
    selectedCompanyShortName,
    selectedCompanyId,
    selectedReferenceId,
    dataCy,
    hasNoneOption,
    validate,
    onAddNew,
  } = props

  const { t } = useTranslate()
  const { appViewStore } = usePlanningStore()
  const customerAutocompleteViewStore = useLocalStore(() => new CustomerAutocompleteViewStore())
  const [resolvedValue, setResolvedValue] = useState<CompanyResponseDto | undefined>(undefined)
  const [inputValue, setInputValue] = useState<string>('')

  useEffect(() => {
    const fetchValue = async () => {
      if (selectedCustomer) {
        setResolvedValue(selectedCustomer)
        return
      }

      if (selectedCompanyId) {
        let company = customerAutocompleteViewStore.items.find(c => c.id === selectedCompanyId)
        if (!company) {
          company = await companyService.getById(selectedCompanyId)
        }
        if (company.id !== resolvedValue?.id) setResolvedValue(company ? { ...company } : undefined)
        return
      }

      if (selectedReferenceId) {
        let company = customerAutocompleteViewStore.items.find(
          c => c.referenceId === selectedReferenceId,
        )
        if (!company) {
          const companies = await companyService.getByReferenceIds([selectedReferenceId])
          company = companies[0]
        }

        if (company.referenceId !== resolvedValue?.referenceId)
          setResolvedValue(company ? { ...company } : undefined)
        return
      }

      if (selectedCompanyShortName) {
        let company = customerAutocompleteViewStore.items.find(
          c => c.shortName === selectedCompanyShortName,
        )

        if (!company) {
          company = await companyService.getByShortName(selectedCompanyShortName)
        }
        setResolvedValue(company ? { ...company } : undefined)
        return
      }

      setResolvedValue(undefined)
    }

    fetchValue().catch((error: AxiosError) => {
      if (error.response?.status === 404) {
        appViewStore.setShowAlert(
          'error',
          t('companyNotFound', 'The company for {company} was not found', {
            company: (label ?? t('customer', 'Customer')).toLocaleLowerCase(),
          }),
        )
      } else {
        appViewStore.setShowAlert('error', error.message)
      }
    })
  }, [
    selectedCustomer,
    selectedCompanyId,
    selectedReferenceId,
    selectedCompanyShortName,
    customerAutocompleteViewStore,
    appViewStore,
    resolvedValue?.id,
    label,
    t,
  ])

  useEffect(() => {
    const fetch = async () => {
      await customerAutocompleteViewStore.fetch(customerAutocompleteViewStore.filter)
    }

    fetch().catch(error => appViewStore.setShowAlert('error', error.message))
  }, [appViewStore, customerAutocompleteViewStore])

  const onSearchFilterChange = async (newFilter: string) => {
    customerAutocompleteViewStore.setLoading(true)
    setInputValue(newFilter)
    customerAutocompleteViewStore.setFilter(newFilter)

    if (newFilter.length) {
      await customerAutocompleteViewStore.fetch(newFilter)
      customerAutocompleteViewStore.setLoading(false)
    }
  }

  const filterOptions = (options: CompanyResponseDto[], { inputValue }: { inputValue: string }) => {
    if (customerAutocompleteViewStore.isLoading || !inputValue || inputValue.trim() === '') {
      return []
    }
    const filtered = matchSorter(options, inputValue.replace(/\s/g, ''), {
      sorter: rankedItems => rankedItems,
      keys: [
        item => (item.shortName ? item.shortName.replace(/\./g, '').replace(/\s/g, '') : ''),
        item => item.name.replace(/\./g, '').replace(/\s/g, ''),
      ],
      threshold: matchSorter.rankings.STARTS_WITH,
    })

    const addNewOption = options.find(opt => opt.id === 0)
    if (addNewOption && !filtered.some(opt => opt.id === 0)) {
      filtered.push(addNewOption)
    }

    return filtered
  }

  const getOptions = () => {
    if (customerAutocompleteViewStore.isLoading || inputValue.length < 1) {
      return []
    }

    let options = customerAutocompleteViewStore.items.filter(
      x => !filterByTypes?.length || x.types?.some(t => filterByTypes.includes(t)),
    )

    options = [...options]

    if (onAddNew) {
      options.push({
        id: 0,
        name: '',
        shortName: '',
        referenceId: 0,
      })
    }

    if (hasNoneOption) {
      options = [
        {
          id: -1,
          name: t('none', 'None'),
          referenceId: 0,
        },
        ...options,
      ]
    }

    return options
  }

  return (
    <TomAutoComplete
      {...props}
      label={label ?? t('customer', 'Customer')}
      optionalLabel={option =>
        option.isDeactivated ? (
          <ColoredLabel color='warning' label={t('inactive', 'Inactive')} />
        ) : null
      }
      getOptionDisabled={option => !!option.isDeactivated}
      disabled={disabled}
      getOptionLabel={customer => customer.name}
      getOptionSubtitle={customer => customer.shortName}
      options={getOptions()}
      defaultValue={resolvedValue}
      onChangeEntity={entity => {
        if (entity?.id === 0) {
          onAddNew?.()
          return
        }
        handleOnChange?.(entity)
      }}
      entityKey='id'
      entityDisplayValue={'name'}
      validate={validate}
      filterOptions={filterOptions}
      data-cy={dataCy ?? 'customer-autocomplete'}
      onChangeFilter={filter => onSearchFilterChange(filter ?? '')}
    />
  )
})
